import { createFileRoute, redirect } from "@tanstack/react-router";
import { z } from "zod";

import { MemberQuotePage } from "@/features/my/quotes";
import { maskSearchFragment } from "@/features/shared/route-masking";
import {
  hasStripeRedirectSecret,
  withoutStripeRedirectSecret,
} from "@/libs/stripe-redirect";

/**
 * The homeowner's quote page (R-1.4). A SIBLING of `my.orders.$orderId.tsx`,
 * not a child: the order detail renders no `<Outlet />`, so a nested route
 * would never draw.
 *
 * `option` is the option on show when the quote offers several (05 H-4). It
 * stays a lenient string: an id the quote no longer offers falls back to the
 * page's own default instead of crashing the route.
 *
 * `step` is `review` while a phone shows Review & Confirm in place of the quote
 * — pushed, so going back returns to the quote. Lenient like `option`: any
 * other value is the quote.
 *
 * `payment_intent` is set when Stripe sends the homeowner back from the bank's
 * approval step of a deposit payment; the page completes that payment once and
 * removes it with Stripe's other redirect keys. Lenient too, and the client
 * secret is never declared here.
 *
 * The client secret leaves the URL before anything renders: `beforeLoad` runs
 * ahead of the member layout, so its tag manager, the page-view event and the
 * sign-in redirect never read it. It reads the raw `location.search`, which
 * still holds the keys the schema does not declare.
 */
const searchSchema = z.object({
  option: z.string().optional().catch(undefined),
  step: z.string().optional().catch(undefined),
  payment_intent: z.string().optional().catch(undefined),
  ...maskSearchFragment,
});

export const Route = createFileRoute(
  "/(member)/(protected)/my/quotes/$quoteId",
)({
  validateSearch: searchSchema,
  beforeLoad: ({ location, params }) => {
    if (!hasStripeRedirectSecret(location.search)) return;
    throw redirect({
      to: "/my/quotes/$quoteId",
      params,
      search: withoutStripeRedirectSecret(location.search),
      replace: true,
    });
  },
  component: MemberQuotePage,
  head: () => ({
    meta: [
      { title: "Birdwatch - Quote" },
      { name: "description", content: "Birdwatch - Quote" },
    ],
  }),
});
